import { useEffect, useState } from 'react';
import * as domainsApi from '../api/domains';
import { useToast } from './toast';
import { Field, Spinner } from './ui';
import DnsRecordsTable from './DnsRecordsTable';
import { cn, ui } from '../lib/ui';

const HOST_ROLE_LABELS = {
  website: 'Website (your template site)',
  portal: 'Portal (members sign in here)',
};

/** One host's DNS records and its status, with a Verify button until it is live. */
function HostRecords({ record, onVerified }) {
  const toast = useToast();
  const [current, setCurrent] = useState(record);
  const [verifying, setVerifying] = useState(false);
  const live = current.status === 'active';

  async function verify() {
    setVerifying(true);
    try {
      const data = await domainsApi.verifyDomain(current.domainId);
      // Verify answers { domain: record }; keep it so the next DNS record
      // (the CNAME once ownership is proven) shows.
      const next = data.domain && typeof data.domain === 'object' ? data.domain : current;
      setCurrent(next);
      if (next.status === 'active') {
        toast.success(`${next.domain} is live`);
        onVerified?.(next);
      } else {
        toast.info(`${next.domain}: ${next.status}. DNS changes can take time to spread.`);
      }
    } catch (err) {
      toast.error(err.message || 'Verification failed - check your DNS records and try again');
    } finally {
      setVerifying(false);
    }
  }

  return (
    <section className="mb-4" aria-label={current.domain}>
      <h3 className="mb-1 text-title-s">
        {HOST_ROLE_LABELS[current.role] || current.role}: <code>{current.domain}</code>
      </h3>
      {live ? (
        <p className={ui.alertSuccess}>Verified and live.</p>
      ) : (
        <>
          <div className="mb-2">
            <DnsRecordsTable records={current.dnsRecords} />
          </div>
          <button
            className={cn(ui.btnSecondary, ui.btnSm)}
            onClick={verify}
            disabled={verifying}
            type="button"
          >
            {verifying ? 'Verifying...' : `Verify DNS for ${current.domain}`}
          </button>
        </>
      )}
    </section>
  );
}

/**
 * The org's own domain (docs/Decisions.md D56 item 2, D60 item 4, D61): the
 * website host shows the org's template site and the portal host the
 * member app. Both names are asked, `www` and `portal` by default, and can
 * be renamed later in Org settings. Used by the org setup wizard and the
 * site builder's launch section.
 *
 * Props: onNext (Continue after adding), onSkip (optional Skip button),
 * renderNav (optional extra buttons, such as the wizard's Back).
 */
export default function HostSetup({ onNext, onSkip, renderNav }) {
  const toast = useToast();
  const [domain, setDomain] = useState('');
  const [websiteLabel, setWebsiteLabel] = useState('www');
  const [portalLabel, setPortalLabel] = useState('portal');
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(null);
  const [checking, setChecking] = useState(true);

  // Coming back to this step (docs/Decisions.md D63 item 5) shows the
  // addresses already added, with their records and Verify buttons, rather
  // than an empty form that would add them twice.
  useEffect(() => {
    let cancelled = false;
    domainsApi
      .listDomains()
      .then(data => {
        const hosts = (data.domains || []).filter(
          d => ['website', 'portal'].includes(d.role) && !['removing', 'failed'].includes(d.status)
        );
        if (!cancelled && hosts.length) setAdded(hosts);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) return <Spinner label="Checking your addresses..." />;

  const base = domain
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '');

  async function handleAdd(e) {
    e.preventDefault();
    if (!base) return;
    setAdding(true);
    try {
      const data = await domainsApi.setupHosts({
        domain: base,
        websiteLabel: websiteLabel.trim(),
        portalLabel: portalLabel.trim(),
      });
      toast.success('Addresses added - configure the DNS records shown below');
      setAdded(data.domains || []);
    } catch (err) {
      toast.error(err.message || 'Failed to add the addresses');
    } finally {
      setAdding(false);
    }
  }

  return (
    <div>
      {!added ? (
        <form onSubmit={handleAdd}>
          <Field
            label="Domain"
            value={domain}
            onChange={e => setDomain(e.target.value)}
            placeholder="example.org"
            hint="The domain your organization owns, without www."
          />
          <div className={cn(ui.grid, ui.gridMd2)}>
            <Field
              label="Website address"
              value={websiteLabel}
              onChange={e => setWebsiteLabel(e.target.value)}
              hint={`Your template site at ${websiteLabel.trim() || 'www'}.${base || 'example.org'}`}
            />
            <Field
              label="Portal address"
              value={portalLabel}
              onChange={e => setPortalLabel(e.target.value)}
              hint={`Members sign in at ${portalLabel.trim() || 'portal'}.${base || 'example.org'}`}
            />
          </div>
          <div className={cn(ui.row, 'mt-4')}>
            <button className={ui.btnPrimary} type="submit" disabled={adding || !base}>
              {adding ? 'Adding...' : 'Add addresses'}
            </button>
            {renderNav ? renderNav() : null}
            {onSkip ? (
              <button className={ui.btnSecondary} onClick={onSkip} type="button">
                Skip
              </button>
            ) : null}
          </div>
        </form>
      ) : (
        <>
          <div className={ui.alertInfo}>
            Add these DNS records where your domain is managed, then choose{' '}
            <strong>Verify DNS</strong> for each address. First the TXT record proves you own the
            domain; then a CNAME record appears to point the address at the platform. DNS changes
            can take up to 48 hours to spread.
          </div>
          {added.map(record => (
            <HostRecords key={record.domainId} record={record} />
          ))}
          {onNext ? (
            <div className={ui.row}>
              <button className={ui.btnPrimary} onClick={onNext} type="button">
                Continue
              </button>
              {renderNav ? renderNav() : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
